// © Microsoft Corporation. All rights reserved.
import React, { useEffect } from 'react';
import { Stack, Toggle, Image, ImageFit } from '@fluentui/react';
import { MicIcon, CallVideoIcon } from '@fluentui/react-icons-northstar';
import { Constants, labelConstants } from '../core/constants';
import {
    VideoStreamRendererView,
    VideoDeviceInfo,
    AudioDeviceInfo,
    LocalVideoStream,
    VideoStreamRenderer
} from '@azure/communication-calling';
import staticMediaSVG from '../assets/staticmedia.svg';
import {
    toggleStyle,
    imgStyles,
    toggleButtonsBarToken,
    localPreviewContainerStyle,
    toggleButtonsBarStyle,
    localPreviewStyle
} from './styles/LocalPreview.styles';
import { IsCandidateMode, IsHiddenMode, IsProctorMode, IsSecondaryStream, IsProctorCameraEnabled} from 'Utils/Utils';
import { GetSelectedCamera, GetSelectedMicrophone } from '../core/sideEffects';

export interface LocalPreviewProps {
    setMic(mic: boolean): void;
    mic: boolean;
    setLocalVideoStream(device: LocalVideoStream | undefined): void;
    videoDeviceInfo: VideoDeviceInfo;
    audioDeviceInfo: AudioDeviceInfo;
    videoDeviceList: VideoDeviceInfo[];
    audioDeviceList: AudioDeviceInfo[];
    localVideoStream: LocalVideoStream;
    setVideoDeviceInfo(device: VideoDeviceInfo): void;
    setAudioDeviceInfo(device: AudioDeviceInfo): void;
}

let rendererView: VideoStreamRendererView | undefined;
let init: boolean = true;

export default (props: LocalPreviewProps): JSX.Element => {
    if (init) {
        if (props.videoDeviceList.length > 0) {
            if (props.videoDeviceList.length < GetSelectedCamera()) {
                props.setVideoDeviceInfo(props.videoDeviceList[0]);
            }
            else {
                let i: number = 0;
                props.videoDeviceList.forEach((data) => {
                    if (i === GetSelectedCamera()) {
                        props.setVideoDeviceInfo(data);
                    }
                    i++;
                });
            }
        }

        if (props.audioDeviceList.length > 0) {
            if (props.audioDeviceList.length < GetSelectedMicrophone()) {
                props.setAudioDeviceInfo(props.audioDeviceList[0]);
            }
            else {
                let i: number = 0;
                props.audioDeviceList.forEach((data) => {
                    if (i === GetSelectedMicrophone()) {
                        props.setAudioDeviceInfo(data);
                    }
                    i++;
                });
            }
        }

        init = false;
    }

    const imageProps = {
        src: staticMediaSVG.toString(),
        imageFit: ImageFit.cover,
        maximizeFrame: true
    };

    const handleLocalVideoOnOff = (_ev: React.MouseEvent<HTMLElement>, checked = false): void => {
        const stream = new LocalVideoStream(props.videoDeviceInfo);
        var shouldCheck = checked;//|| IsCandidateMode();
        if (checked) {
            _ev.currentTarget.style.backgroundColor = "rgb(255,50,0)";
        }
        else {
            _ev.currentTarget.style.backgroundColor = "rgb(255,255,255)";
        }
        props.setLocalVideoStream(shouldCheck ? stream : undefined);
    };
    const handleLocalMicOnOff = (_ev: React.MouseEvent<HTMLElement>, checked = false): void => {
        var shouldCheck = checked || IsCandidateMode();
        props.setMic(shouldCheck);
    };

    useEffect(() => {
        (async (): Promise<void> => {
            if (props.localVideoStream) {
                
                    const renderer: VideoStreamRenderer = new VideoStreamRenderer(props.localVideoStream);
                    rendererView = await renderer.createView({ scalingMode: 'Crop', isMirrored: true });

                    var videoList = rendererView.target.getElementsByTagName("video");
                    for (let i = 0; i < videoList.length; i++) {
                        videoList[i].attributes.setNamedItem(document.createAttribute("disablePictureInPicture"));
                    }

                    const container = document.getElementById(Constants.CONFIGURATION_LOCAL_VIDEO_PREVIEW_ID);

                    if (container && container.childElementCount === 0) {
                        container.appendChild(rendererView.target);
                    }
               
            }
        })();

        return (): void => {
            if (rendererView) {
                rendererView.dispose();
                rendererView = undefined;
            }
        };
    }, [props.localVideoStream]);
    return (
        <Stack className={localPreviewContainerStyle}>
            <Stack
                horizontalAlign="center"
                verticalAlign="center"
                id={Constants.CONFIGURATION_LOCAL_VIDEO_PREVIEW_ID}
                className={localPreviewStyle}
            >
                {!props.localVideoStream && <Image styles={imgStyles} {...imageProps} aria-label="Local video preview image" />}
            </Stack>
            <Stack
                horizontal
                horizontalAlign="center"
                verticalAlign="center"
                tokens={toggleButtonsBarToken}
                className={toggleButtonsBarStyle}
            >
                <CallVideoIcon size="medium" style={{ display: (IsProctorMode() && !IsProctorCameraEnabled()) ||IsSecondaryStream() ? 'none' : 'block' }} />
                <Toggle
                    checked={props.localVideoStream !== undefined || IsCandidateMode() || IsSecondaryStream()}
                    styles={toggleStyle}
                    disabled={!props.videoDeviceInfo || props.videoDeviceList.length === 0 || IsCandidateMode() || IsHiddenMode() || IsSecondaryStream()}
                    onChange={handleLocalVideoOnOff}
                    ariaLabel={labelConstants.VideoIcon}
                    hidden={(IsProctorMode() && !IsProctorCameraEnabled())|| IsSecondaryStream()}
                />
                <MicIcon size="medium" style={{ display: IsSecondaryStream() ? 'none' : 'block' }}/>
                <Toggle
                    checked={props.mic || IsCandidateMode()}
                    styles={toggleStyle}
                    disabled={!props.audioDeviceInfo || props.audioDeviceList.length === 0 || IsCandidateMode() || IsHiddenMode() || IsSecondaryStream()}
                    onChange={handleLocalMicOnOff}
                    ariaLabel={labelConstants.MicrophoneIcon}
                    hidden={IsSecondaryStream()}
                />

            </Stack>
        </Stack>
    );
};